import { useMemo } from 'react';
import { randomString } from 'remeda';
import {
  ChipFilterDefinition,
  RawFilter,
  useChipFilters,
} from 'src/components/FilterChips/use-chip-filters';
import { UIType } from 'src/graphql/entities/custom-view';

import { useGetNotificationEvents } from 'src/graphql/entities/notification-preference';
import { ConditionInput, ConditionOperator } from 'src/graphql/types';
import { sortAlphabetically } from 'src/utils/sorting';

export enum InboxFilterAttribute {
  notificationEventId = 'notificationEventId',
}

export const useInboxFilters = () => {
  const { notificationEvents } = useGetNotificationEvents();

  /* single field definition */
  const notificationEventDef: ChipFilterDefinition<InboxFilterAttribute.notificationEventId> =
    useMemo(
      () => ({
        attribute: InboxFilterAttribute.notificationEventId,
        field: {
          value: InboxFilterAttribute.notificationEventId,
          label: 'Type',
          pluralLabel: 'Types',
          uiType: UIType.List,
          optionsQuery: 'notificationEvents',
        },
        defaultOperator: ConditionOperator.In, // multiselect by default
        operators: [ConditionOperator.In, ConditionOperator.NotIn],
        useOptions: () =>
          notificationEvents
            .map((n) => ({ value: n.id, label: n.name }))
            .sort(sortAlphabetically('label')),
      }),
      [notificationEvents],
    );

  const allFields = useMemo(() => [notificationEventDef], [notificationEventDef]);

  const {
    filters: rawFilters,
    chipConditions,
    chipQueryOptions,
    handleChipConditionsChange,
    deleteChipCondition,
  } = useChipFilters(allFields);

  const filterMenuOptions = useMemo(() => {
    return [
      {
        label: 'Filter by notification type',
        onClick: () => {
          handleChipConditionsChange([
            {
              id: randomString(10),
              // @ts-expect-error need to improve useChipFilters typings to allow generics
              field: notificationEventDef.field,
              operator: notificationEventDef.defaultOperator,
            },
          ]);
        },
      },
    ];
  }, [notificationEventDef, handleChipConditionsChange]);

  const filters = useMemo(() => sanitizeFiltersForApi(rawFilters), [rawFilters]);

  return {
    filters,
    chipConditions,
    chipQueryOptions,
    handleChipConditionsChange,
    deleteChipCondition,
    filterMenuOptions,
  };
};

function sanitizeFiltersForApi(filters: RawFilter<InboxFilterAttribute>[]): ConditionInput[] {
  return filters
    .map((i) => ({
      ...i,
      value: Array.isArray(i.value) ? i.value.join(',') : (i.value as string),
    }))
    .filter((i) => !!i.value);
}
